/**
 * WorkGroupSection — agrupar/desagrupar trabalhos (Fase 3).
 *
 * Mora DENTRO do accordion da trilha: agrupar é exceção, não rotina. A trilha
 * derivada da corrente resolve a esmagadora maioria dos casos; isto existe para
 * os dois que ela não expressa — dois orçamentos para o mesmo serviço (o cliente
 * recusou a v1) e um contrato avulso, que não alcança a OS.
 *
 * **Online-only**, pela mesma razão do kit (#65) e da despesa cobrável (#76): a
 * fila offline mapeia UMA operação para UM uuid e não sabe resolver o par de
 * documentos de um vínculo. Recusa com aviso em vez de enfileirar algo que não
 * sincroniza.
 */
import { useMemo, useState } from 'react';
import { Link2, Unlink } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useWorks } from '@/hooks/useWorks';
import { useWorkLinks, isGroupableRoot } from '@/hooks/useWorkLinks';
import type { WorkRoot } from '@/lib/work';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export interface WorkGroupSectionProps {
  /** As raízes do trabalho atual (a primeira é a que a tela está mostrando). */
  roots: WorkRoot[];
  onChanged?: () => void;
}

export function WorkGroupSection({ roots, onChanged }: WorkGroupSectionProps) {
  const { isOnline } = useNetworkStatus();
  const { group, ungroup } = useWorkLinks();
  const { entries } = useWorks();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  const root = roots[0];
  const grouped = roots.length > 1;

  /** Candidatos: os outros trabalhos da empresa, sem os que já são este. */
  const candidates = useMemo(() => {
    const mine = new Set(roots.map((r) => `${r.kind}:${r.id}`));
    const term = search.trim().toLowerCase();
    return entries
      .filter((e) => !mine.has(`${e.root.kind}:${e.root.id}`))
      .filter((e) => isGroupableRoot(e.root.kind))
      .filter(
        (e) =>
          !term ||
          e.work.title.toLowerCase().includes(term) ||
          (e.work.clientName ?? '').toLowerCase().includes(term),
      )
      .slice(0, 40);
  }, [entries, roots, search]);

  if (!root || !isGroupableRoot(root.kind)) return null;

  const requireOnline = (): boolean => {
    if (isOnline) return true;
    toast.warning('Agrupar trabalhos exige conexão.');
    return false;
  };

  const handleGroup = async (other: WorkRoot) => {
    if (!requireOnline()) return;
    try {
      await group.mutateAsync({ a: root, b: other });
      setPickerOpen(false);
      setSearch('');
      toast.success('Trabalhos agrupados.');
      onChanged?.();
    } catch (error) {
      console.error('Error grouping works:', error);
      toast.error('Não foi possível agrupar os trabalhos.');
    }
  };

  const handleUngroup = async () => {
    if (!requireOnline()) return;
    try {
      await ungroup.mutateAsync(root);
      toast.success('Trabalho separado.');
      onChanged?.();
    } catch (error) {
      console.error('Error ungrouping work:', error);
      toast.error('Não foi possível separar o trabalho.');
    }
  };

  return (
    <>
      {grouped ? (
        <div className="space-y-1">
          <Button
            variant="outline"
            className="w-full justify-start"
            onClick={handleUngroup}
            disabled={ungroup.isPending}
          >
            <Unlink className="mr-2 h-4 w-4" />
            Separar deste trabalho
          </Button>
          <p className="pl-1 text-xs text-muted-foreground">
            Agrupado com {roots.length - 1}{' '}
            {roots.length - 1 === 1 ? 'outro documento' : 'outros documentos'}.
          </p>
        </div>
      ) : (
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => setPickerOpen(true)}
        >
          <Link2 className="mr-2 h-4 w-4" />
          Agrupar com outro trabalho
        </Button>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[85dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agrupar com outro trabalho</DialogTitle>
            <DialogDescription>
              Os documentos dos dois passam a aparecer numa trilha só. Útil quando você refez o
              orçamento e os dois são do mesmo serviço.
            </DialogDescription>
          </DialogHeader>

          <Input
            placeholder="Buscar por título ou cliente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="space-y-1.5">
            {candidates.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum outro trabalho encontrado.
              </p>
            ) : (
              candidates.map((entry) => (
                <button
                  key={`${entry.root.kind}-${entry.root.id}`}
                  type="button"
                  disabled={group.isPending}
                  onClick={() => handleGroup(entry.root)}
                  className="flex w-full items-center justify-between gap-3 rounded-md border p-2.5 text-left transition hover:bg-muted disabled:opacity-60"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium">{entry.work.title}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {entry.work.clientName ? `${entry.work.clientName} · ` : ''}
                      {entry.work.phaseLabel}
                    </span>
                  </span>
                  {entry.work.total > 0 && (
                    <span className="shrink-0 text-sm font-semibold">
                      {formatCurrency(entry.work.total)}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
